import React, { useEffect } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { APP_NAME } from "Constants/documentationUrls";
import { CloseButton } from "ds/Button";
import { useLocalForage } from "Hooks/useLocalForage";
import { getOrganizationId } from "Libs/utils";
import { organizationSelector } from "Reducers/organization";
import {
  currentDunningEventSelector,
  loadDunning
} from "Reducers/organization/dunning";
import { loggedInMemberDataSelector } from "Reducers/organization/loggedInMember/loggedInMember.selectors";
import { loadLoggedInMember } from "Reducers/organization/loggedInMember/thunks/loadLoggedInMember";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import {
  BannerContent,
  Button,
  CloseWrapper,
  Layout
} from "./DunningBanner.styles";

type DunningBannerProps = {
  organizationName: string;
};

const DunningBanner = ({ organizationName }: DunningBannerProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const organizationId = useAppSelector(state =>
    getOrganizationId(state, organizationName)
  );

  const organization = useAppSelector(state =>
    organizationSelector(state, { organizationId })
  );

  const status = organization?.status;

  const currentEvent = useAppSelector(state =>
    currentDunningEventSelector(state, {
      organizationId,
      status
    })
  );

  const memberData = useAppSelector(loggedInMemberDataSelector);

  const { value: lastClosedBanner, setValue: setLastClosedBanner } =
    useLocalForage<Record<string, number>>("dunning-banner-closed");

  useEffect(() => {
    if (!organizationId) return;
    dispatch(loadDunning({ organizationId }));
    dispatch(loadLoggedInMember(organizationId));
  }, [dispatch, organizationId]);

  if (
    (lastClosedBanner?.[organizationName] ?? -1) >
    new Date().getTime() - 24 * 60 * 60 * 1000 // 24 hours in milliseconds
  ) {
    return null;
  }

  let dunningPhase:
    | "restricted"
    | "suspended_days"
    | "suspended_tomorrow"
    | "suspended_within_day"
    | "deleted_tomorrow"
    | "deleted_within_day"
    | "deleted_days"
    | "suspended"
    | undefined;

  if (currentEvent?.type === "suspend" && currentEvent.days_until === 1) {
    dunningPhase = "suspended_tomorrow";
  } else if (
    currentEvent?.type === "suspend" &&
    currentEvent.days_until === 0
  ) {
    dunningPhase = "suspended_within_day";
  } else if (currentEvent?.type === "suspend") {
    dunningPhase = "suspended_days";
  } else if (
    currentEvent?.type === "revoke" &&
    currentEvent?.days_until === 1
  ) {
    dunningPhase = "deleted_tomorrow";
  } else if (currentEvent?.type === "revoke" && currentEvent.days_until === 0) {
    dunningPhase = "deleted_within_day";
  } else if (currentEvent?.type === "revoke" && currentEvent.days_until > 1) {
    dunningPhase = "deleted_days";
  } else if (currentEvent?.type === "revoke") {
    dunningPhase = "suspended";
  } else if (status === "deleted") {
    dunningPhase = "suspended";
  }

  if (!dunningPhase || !currentEvent) {
    return null;
  }

  const hasBillingPermission =
    (memberData?.member.permissions?.includes("billing") ||
      memberData?.member.owner) ??
    false;

  const handleClose = (e: React.MouseEvent) => {
    e?.preventDefault();
    setLastClosedBanner({
      ...lastClosedBanner,
      [organizationName]: new Date().getTime()
    });
  };

  return (
    <Layout>
      <BannerContent>
        <FormattedMessage
          id={`banner.dunning.${dunningPhase}.${
            hasBillingPermission ? "update" : "ask"
          }`}
          values={{ num: currentEvent?.days_until, appName: APP_NAME }}
        />
        {hasBillingPermission && (
          <Button
            href={`/${organizationName}/-/billing/details`}
            analyticId="dunning.update_billing"
          >
            {intl.formatMessage({
              id: "dunning.update_billing"
            })}
          </Button>
        )}
      </BannerContent>
      <CloseWrapper>
        <CloseButton onClick={handleClose} />
      </CloseWrapper>
    </Layout>
  );
};

export default DunningBanner;
